import React from 'react';
import { Popover, Stack, Box, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RecipeCostEstimate } from '../../types/mealPlan';

const formatUsd = (n: number): string => `$${n.toFixed(2)}`;

interface CostPerServingPopoverProps {
  costEstimate: RecipeCostEstimate;
  // Optional dine-out comparison; the savings block only renders when the
  // dine-out price is above the mid cost-per-serving.
  dineOut?: { usd: number; reasoning: string };
  // Trigger text size (defaults to 'sm' to match the recipe meta rows).
  size?: string;
  // Render the trigger dimmed (used in the recipe details "Cost" stat row).
  dimmed?: boolean;
}

/**
 * Cost-per-serving trigger + popover, shared by the meal plan RecipeCard and the
 * RecipeDetailsView. Renders a "~$X.XX/serving" trigger that opens a light,
 * theme-styled popover breaking the estimate into value / mid / premium tiers
 * and (when available) how much cooking at home saves vs. dining out.
 *
 * Uses Mantine Popover (not Tooltip) so it sits on the app's normal light card
 * surface with the app's rounding, and manages its own open state — closing on
 * outside click and toggling on trigger click. The trigger stops pointer/click
 * propagation so it doesn't also trigger card drag/flip in the carousel.
 */
export const CostPerServingPopover: React.FC<CostPerServingPopoverProps> = ({
  costEstimate,
  dineOut,
  size = 'sm',
  dimmed = false,
}) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const cost = costEstimate.perServing;
  const hasSavings = dineOut && dineOut.usd > cost.mid;

  return (
    <Popover
      width={260}
      position="bottom"
      withArrow
      opened={opened}
      onChange={(o) => { if (!o) close(); }}
    >
      <Popover.Target>
        <Text
          component="span"
          size={size}
          c={dimmed ? 'dimmed' : undefined}
          style={{ cursor: 'pointer', pointerEvents: 'auto', width: 'fit-content', display: 'inline-block' }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          ~
          <Text
            component="span"
            inherit
            style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '2px' }}
          >
            {formatUsd(cost.mid)}/serving
          </Text>
        </Text>
      </Popover.Target>
      <Popover.Dropdown onClick={(e) => e.stopPropagation()} p="sm">
        <Stack gap={4}>
          <Text size="xs" fw={700}>Cost per serving</Text>
          <Box>
            <Box style={{ display: 'flex' }}>
              <Text size="xs" c="dimmed" style={{ flex: 1, textAlign: 'left' }}>Value</Text>
              <Text size="xs" fw={600} style={{ flex: 1, textAlign: 'center' }}>Mid</Text>
              <Text size="xs" c="dimmed" style={{ flex: 1, textAlign: 'right' }}>Premium</Text>
            </Box>
            {/* Value ── mid ── premium mini range, styled for a light surface */}
            <Box style={{ position: 'relative', height: 14, margin: '4px 0' }}>
              <Box style={{ position: 'absolute', left: 6, right: 6, top: 6, height: 2, background: 'var(--mantine-color-gray-3)' }} />
              <Box style={{ position: 'absolute', left: 0, top: 3, width: 8, height: 8, borderRadius: '50%', background: 'var(--mantine-color-gray-4)' }} />
              <Box style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, width: 14, height: 14, borderRadius: '50%', background: 'var(--mantine-color-orange-5)', border: '2px solid var(--mantine-color-body)' }} />
              <Box style={{ position: 'absolute', right: 0, top: 3, width: 8, height: 8, borderRadius: '50%', background: 'var(--mantine-color-gray-4)' }} />
            </Box>
            <Box style={{ display: 'flex' }}>
              <Text size="xs" c="dimmed" style={{ flex: 1, textAlign: 'left' }}>{formatUsd(cost.value)}</Text>
              <Text size="xs" fw={700} style={{ flex: 1, textAlign: 'center' }}>{formatUsd(cost.mid)}</Text>
              <Text size="xs" c="dimmed" style={{ flex: 1, textAlign: 'right' }}>{formatUsd(cost.premium)}</Text>
            </Box>
          </Box>
          {hasSavings && (
            <>
              <Text size="xs" mt={6}>
                <Text component="span" size="xs" fw={700}>Dining out:</Text> ~{formatUsd(dineOut!.usd)}/serving
              </Text>
              <Text size="xs" c="teal.7" fw={600}>
                Save ~{formatUsd(dineOut!.usd - cost.mid)}/serving cooking at home
              </Text>
              <Text size="xs" c="dimmed" fs="italic">
                {dineOut!.reasoning}
              </Text>
            </>
          )}
          <Text fz={10} c="dimmed" fs="italic" mt={6}>
            Estimated from reference Kroger store prices; actual prices vary by location.
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
